/**
 * Figma PDP buy box: custom full-width dropdown + engraving + size guide modals.
 * Keeps native <select> in the DOM for variant-selects / cart line items.
 */
(function () {
  /** Remember explicit size picks across variant-selects HTML swaps */
  const sizeConfirmedMemory = new Map();
  /** Chain size remembered across HTML swaps: { value, id, label, index } or legacy string */
  const chainSizeMemory = new Map();
  /** Chain type only counts after user picks (not Shopify auto-select) */
  const chainTypeUserMemory = new Map();
  /** Remembered chain type option value */
  const chainTypeValueMemory = new Map();

  function productMemoryKey(el) {
    const form = el?.closest?.('product-info') || el?.closest?.('.product') || document.querySelector('product-info');
    return form?.dataset?.productId || form?.getAttribute?.('data-product-id') || 'product';
  }

  function normalizeOptionValue(value) {
    return String(value || '')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/g, "'")
      .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
      .replace(/[\u2018\u2019\u2032]/g, "'")
      .replace(/["']/g, '') // inch marks vary — compare without quotes
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function forceSelectOption(select, opt) {
    if (!select || !opt) return false;
    const idx = Array.from(select.options).indexOf(opt);
    if (idx < 0) return false;
    for (let i = 0; i < select.options.length; i++) {
      const o = select.options[i];
      const on = i === idx;
      o.selected = on;
      if (on) o.setAttribute('selected', 'selected');
      else o.removeAttribute('selected');
    }
    try {
      select.selectedIndex = idx;
    } catch (_) {}
    return select.options[select.selectedIndex] === opt || opt.selected === true;
  }

  function rememberChainSize(pickerOrEl, opt, optionLi) {
    const key = productMemoryKey(pickerOrEl);
    const labelEl = optionLi && optionLi.querySelector('.product-buybox-figma__chain-option__label');
    chainSizeMemory.set(key, {
      value: opt ? opt.value : '',
      id: (opt && opt.getAttribute('data-option-value-id')) || (optionLi && optionLi.getAttribute('data-option-value-id')) || '',
      label: (labelEl ? labelEl.textContent : opt ? opt.textContent : '').trim(),
      index: opt && opt.parentElement ? Array.from(opt.parentElement.options).indexOf(opt) : -1,
    });
  }

  function findRememberedChainOption(select, remembered) {
    if (!select || remembered == null) return null;
    if (typeof remembered === 'string') {
      return Array.from(select.options).find(
        (o) => normalizeOptionValue(o.value) === normalizeOptionValue(remembered)
      );
    }
    let match = null;
    if (remembered.id) {
      match = Array.from(select.options).find((o) => o.getAttribute('data-option-value-id') === remembered.id);
      if (match) return match;
    }
    if (remembered.index >= 0 && select.options[remembered.index]) {
      return select.options[remembered.index];
    }
    if (remembered.value) {
      match = Array.from(select.options).find(
        (o) => normalizeOptionValue(o.value) === normalizeOptionValue(remembered.value)
      );
      if (match) return match;
    }
    if (remembered.label) {
      match = Array.from(select.options).find(
        (o) => normalizeOptionValue(o.textContent) === normalizeOptionValue(remembered.label)
      );
      if (match) return match;
    }
    return null;
  }

  function paintChainFace(wrap, label) {
    if (!wrap || !label) return;
    const current = wrap.querySelector('.product-buybox-figma__select-current');
    if (!current) return;
    current.textContent = label;
    current.classList.remove('product-buybox-figma__chain-select__placeholder');
  }

  function syncSelectFace(select) {
    const wrap = select.closest('[data-figma-custom-select]');
    if (!wrap) return;
    const current = wrap.querySelector('.product-buybox-figma__select-current');
    const opt = select.options[select.selectedIndex];
    const val = opt ? opt.value : '';
    const chainPicker = wrap.closest('[data-figma-chain-picker]');
    const sizePicker = wrap.closest('[data-figma-size-picker]');
    const chainConfirmed = chainPicker && chainPicker.getAttribute('data-figma-chain-confirmed') === 'true';
    const sizeConfirmed = sizePicker && sizePicker.getAttribute('data-figma-size-confirmed') === 'true';
    const isChain = Boolean(wrap.hasAttribute('data-figma-chain-select'));
    const isSize = Boolean(wrap.hasAttribute('data-figma-size-select'));

    if (current) {
      if ((isChain && !chainConfirmed) || (isSize && !sizeConfirmed)) {
        const placeholder = current.getAttribute('data-placeholder') || 'Please select a size';
        current.textContent = placeholder;
        current.classList.add('product-buybox-figma__chain-select__placeholder');
      } else if (opt) {
        const faceLabel =
          opt.getAttribute('data-face-label') ||
          (opt.textContent || '').split(' — ')[0].trim() ||
          opt.textContent.trim();
        current.textContent = faceLabel;
        current.classList.remove('product-buybox-figma__chain-select__placeholder');
      }
    }

    wrap.querySelectorAll('.product-buybox-figma__select-option, .product-buybox-figma__chain-option').forEach((li) => {
      const liVal = li.dataset.value != null ? li.dataset.value : li.getAttribute('data-value') || '';
      const match = isChain
        ? chainConfirmed && normalizeOptionValue(liVal) === normalizeOptionValue(val)
        : isSize
          ? sizeConfirmed && normalizeOptionValue(liVal) === normalizeOptionValue(val)
          : normalizeOptionValue(liVal) === normalizeOptionValue(val);
      li.setAttribute('aria-selected', match ? 'true' : 'false');
    });

    if (chainPicker && chainConfirmed) {
      const err = chainPicker.querySelector('[data-figma-chain-error]');
      if (err) err.hidden = true;
      chainPicker.classList.remove('is-invalid');
    }
    if (sizePicker && sizeConfirmed) {
      sizePicker.classList.remove('is-invalid');
    }
  }

  function syncSelectOpenBodyClass() {
    const anyOpen = document.querySelector('[data-figma-custom-select].is-open');
    document.body.classList.toggle('buybox-select-open', Boolean(anyOpen));
  }

  function setSelectOpen(wrap, open) {
    const btn = wrap.querySelector('.product-buybox-figma__select-face');
    const panel = wrap.querySelector('.product-buybox-figma__select-panel');
    if (!btn || !panel) return;
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.hidden = !open;
    wrap.classList.toggle('is-open', open);
    syncSelectOpenBodyClass();
    if (open) bindCustomSelectPanel(wrap);
  }

  /**
   * Bind each option row for mouse/keyboard and iOS touch.
   * The touch path uses a movement threshold so scrolling the list never selects a row,
   * and suppresses Safari's delayed synthetic click after a successful touch pick.
   */
  function bindCustomSelectPanel(wrap) {
    if (!wrap) return;
    const panel = wrap.querySelector('.product-buybox-figma__select-panel');
    if (!panel) return;

    panel.querySelectorAll('.product-buybox-figma__select-option, .product-buybox-figma__chain-option').forEach((li) => {
      if (li._figmaPickHandler) {
        li.removeEventListener('click', li._figmaPickHandler);
        if (li._figmaTouchStartHandler) li.removeEventListener('touchstart', li._figmaTouchStartHandler);
        if (li._figmaTouchMoveHandler) li.removeEventListener('touchmove', li._figmaTouchMoveHandler);
        if (li._figmaTouchEndHandler) li.removeEventListener('touchend', li._figmaTouchEndHandler);
        li.removeEventListener('keydown', li._figmaKeyHandler);
      }

      const pick = function (event) {
        // touchend below already handled this gesture on iOS Safari.
        if (event?.type === 'click' && li._figmaLastTouchPick && Date.now() - li._figmaLastTouchPick < 700) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        if (event) {
          event.stopPropagation();
        }
        applyCustomSelectOption(wrap, li);
      };
      const onKey = function (event) {
        if (event.key === 'Enter' || event.key === ' ') pick(event);
      };

      let touchStartX = 0;
      let touchStartY = 0;
      let touchMoved = false;
      const onTouchStart = function (event) {
        const touch = event.touches && event.touches[0];
        if (!touch) return;
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
        touchMoved = false;
      };
      const onTouchMove = function (event) {
        const touch = event.touches && event.touches[0];
        if (!touch) return;
        if (Math.abs(touch.clientX - touchStartX) > 10 || Math.abs(touch.clientY - touchStartY) > 10) {
          touchMoved = true;
        }
      };
      const onTouchEnd = function (event) {
        if (touchMoved) return;
        li._figmaLastTouchPick = Date.now();
        event.preventDefault();
        event.stopPropagation();
        applyCustomSelectOption(wrap, li);
      };

      li._figmaPickHandler = pick;
      li._figmaKeyHandler = onKey;
      li._figmaTouchStartHandler = onTouchStart;
      li._figmaTouchMoveHandler = onTouchMove;
      li._figmaTouchEndHandler = onTouchEnd;
      li.dataset.figmaOptBound = '1';
      li.style.cursor = 'pointer';
      li.addEventListener('click', pick);
      li.addEventListener('touchstart', onTouchStart, { passive: true });
      li.addEventListener('touchmove', onTouchMove, { passive: true });
      li.addEventListener('touchend', onTouchEnd, { passive: false });
      li.addEventListener('keydown', onKey);
    });
  }

  function closeAllSelectPanels(exceptWrap) {
    document.querySelectorAll('[data-figma-custom-select]').forEach((wrap) => {
      if (exceptWrap && wrap === exceptWrap) return;
      setSelectOpen(wrap, false);
    });
  }

  function restoreChainSizeFromMemory(wrap, select) {
    const chainPicker = wrap?.closest?.('[data-figma-chain-picker]');
    if (!chainPicker || chainPicker.getAttribute('data-figma-chain-kind') !== 'size') return;
    if (!select) return;
    const pKey = productMemoryKey(wrap);
    const remembered = chainSizeMemory.get(pKey);
    if (remembered == null) return;
    const matchOpt = findRememberedChainOption(select, remembered);
    if (!matchOpt) return;

    forceSelectOption(select, matchOpt);
    chainPicker.setAttribute('data-figma-chain-confirmed', 'true');
    const label =
      (typeof remembered === 'object' && remembered.label) || matchOpt.textContent.trim();
    paintChainFace(wrap, label);
  }

  function enrichChainSizeNativeLabels(wrap) {
    if (!wrap) return;
    const picker = wrap.closest('[data-figma-chain-picker]');
    if (!picker || picker.getAttribute('data-figma-chain-kind') !== 'size') return;
    const select = wrap.querySelector('[data-figma-native-select]');
    if (!select) return;
    const hasPlaceholder =
      select.options[0] && select.options[0].getAttribute('data-placeholder') === 'true';
    Array.from(select.options).forEach((opt, i) => {
      if (opt.getAttribute('data-placeholder') === 'true') return;
      const listIndex = hasPlaceholder ? i - 1 : i;
      if (listIndex < 0) return;
      const li = wrap.querySelector(
        '.product-buybox-figma__chain-option[data-option-index="' + listIndex + '"]'
      );
      if (!li) return;
      const labelEl = li.querySelector('.product-buybox-figma__chain-option__label');
      const base = (labelEl ? labelEl.textContent : opt.getAttribute('data-face-label') || opt.textContent || '').trim();
      if (!base) return;
      opt.setAttribute('data-face-label', base);
      const included = li.querySelector('.product-buybox-figma__chain-option__included');
      const priceEl = li.querySelector('[data-figma-chain-shopify-price]');
      if (included) {
        opt.textContent = base + ' — Included';
      } else if (priceEl && priceEl.textContent.trim()) {
        opt.textContent = base + ' — ' + priceEl.textContent.trim();
      } else {
        opt.textContent = base;
      }
    });
  }

  function initCustomSelect(wrap) {
    const select = wrap.querySelector('[data-figma-native-select]');
    const btn = wrap.querySelector('.product-buybox-figma__select-face');
    const panel = wrap.querySelector('.product-buybox-figma__select-panel');
    if (!select || !btn || !panel) return;

    // ALWAYS restore after variant HTML swaps — skipping this made Size taps look like no-ops
    restoreChainSizeFromMemory(wrap, select);
    enrichChainSizeNativeLabels(wrap);

    const sizePicker = wrap.closest('[data-figma-size-picker]');
    if (sizePicker && select.hasAttribute('data-figma-size-native')) {
      const key = selectMemoryKey(select);
      const remembered = key && sizeConfirmedMemory.get(key);
      if (remembered) {
        const matchOpt = Array.from(select.options).find(
          (o) => o.value === remembered && !o.disabled && o.getAttribute('data-placeholder') !== 'true'
        );
        if (matchOpt) {
          select.value = remembered;
          Array.from(select.options).forEach((o) => {
            if (o === matchOpt) o.setAttribute('selected', 'selected');
            else o.removeAttribute('selected');
          });
          sizePicker.setAttribute('data-figma-size-confirmed', 'true');
        }
      }
    }

    // Unconfirmed ring size: strip default selected so cart/API never keep phantom US 4
    if (sizePicker && sizePicker.getAttribute('data-figma-size-confirmed') !== 'true') {
      const key = selectMemoryKey(select);
      const remembered = key && sizeConfirmedMemory.get(key);
      if (!remembered) {
        Array.from(select.options).forEach((o) => {
          o.removeAttribute('selected');
          o.selected = false;
        });
        try {
          select.selectedIndex = -1;
        } catch (_) {}
        select.value = '';
      }
    }

    syncSelectFace(select);
    bindCustomSelectPanel(wrap);

    if (wrap.dataset.figmaSelectBound === '1') return;
    wrap.dataset.figmaSelectBound = '1';

    // Face toggle on the button itself — click only (pointerup + preventDefault broke iOS taps)
    if (btn.dataset.figmaFaceBound !== '1') {
      btn.dataset.figmaFaceBound = '1';
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (wrap._figmaJustPicked && Date.now() - wrap._figmaJustPicked < 700) return;
        toggleCustomSelectFromFace(wrap, btn);
      });
    }

    // Keep hidden select out of tab order; do not steal focus
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');
    select.addEventListener('change', () => syncSelectFace(select));
  }

  function selectMemoryKey(select) {
    if (!select) return '';
    const form = select.closest('product-info') || select.closest('.product') || document;
    const productId = form.dataset?.productId || form.getAttribute?.('data-product-id') || '';
    // Do not include select.id — it can change after HTML swaps and break memory restore
    return [productId, select.getAttribute('name') || ''].join('|');
  }

  /** Match <li> → <option>: DOM/index first (mobile-safe), then option-value-id / value. */
  function findOptionForListItem(select, optionLi) {
    if (!select || !optionLi) return null;

    // 1) Sibling index ↔ options index (native chain is 1:1 from the same Liquid loop)
    const panel = optionLi.parentElement;
    if (panel) {
      const lis = Array.from(panel.children).filter((el) =>
        el.classList &&
        (el.classList.contains('product-buybox-figma__chain-option') ||
          el.classList.contains('product-buybox-figma__select-option'))
      );
      const liIndex = lis.indexOf(optionLi);
      if (liIndex >= 0) {
        const hasPlaceholder =
          select.options[0] && select.options[0].getAttribute('data-placeholder') === 'true';
        const optIndex = hasPlaceholder ? liIndex + 1 : liIndex;
        if (select.options[optIndex]) return select.options[optIndex];
      }
    }

    // 2) Explicit data-option-index
    const idxAttr = optionLi.getAttribute('data-option-index');
    if (idxAttr != null && idxAttr !== '') {
      const byAttr = Array.from(select.options).find(
        (o) => o.getAttribute('data-option-index') === idxAttr
      );
      if (byAttr) return byAttr;
      const idx = parseInt(idxAttr, 10);
      if (!Number.isNaN(idx) && select.options[idx]) return select.options[idx];
    }

    // 3) Option value id
    const optionId = optionLi.getAttribute('data-option-value-id');
    if (optionId) {
      const byId = Array.from(select.options).find(
        (o) => o.getAttribute('data-option-value-id') === optionId
      );
      if (byId) return byId;
    }

    // 4) Normalized value / label
    const raw =
      optionLi.dataset.value != null
        ? optionLi.dataset.value
        : optionLi.getAttribute('data-value') || '';
    const target = normalizeOptionValue(raw);
    let opt = Array.from(select.options).find((o) => normalizeOptionValue(o.value) === target);
    if (opt) return opt;

    const labelEl = optionLi.querySelector(
      '.product-buybox-figma__chain-option__label, .product-buybox-figma__select-option-label'
    );
    const labelText = normalizeOptionValue(labelEl ? labelEl.textContent : optionLi.textContent);
    if (labelText) {
      opt = Array.from(select.options).find(
        (o) =>
          normalizeOptionValue(o.value) === labelText ||
          normalizeOptionValue(o.textContent) === labelText
      );
      if (opt) return opt;
    }

    return null;
  }

  function getProductVariants(root) {
    const el = root.querySelector('[data-product-variants]');
    if (!el) return null;
    try {
      const data = JSON.parse(el.textContent);
      return Array.isArray(data) ? data : null;
    } catch (_) {
      return null;
    }
  }

  /** Immediately sync hidden variant id from current confirmed selections (mobile-critical). */
  function syncProductVariantIdFromSelections(scope) {
    const root =
      (scope && scope.querySelector && scope.tagName === 'PRODUCT-INFO' && scope) ||
      scope?.closest?.('product-info') ||
      document.querySelector('product-info');
    if (!root) return null;

    const variants = getProductVariants(root);
    if (!variants || !variants.length) return null;

    const values = [];
    root.querySelectorAll('variant-selects fieldset input[type="radio"]:checked').forEach((input) => {
      if (input.value) values.push(normalizeOptionValue(input.value));
    });

    root.querySelectorAll('variant-selects select:not([name^="properties["])').forEach((select) => {
      const chainPicker = select.closest('[data-figma-chain-picker]');
      if (chainPicker) {
        if (!chainPicker.hasAttribute('data-figma-chain-native-option')) return;
        if (chainPicker.getAttribute('data-figma-chain-confirmed') !== 'true') return;
      }
      const sizePicker = select.closest('[data-figma-size-picker]');
      if (sizePicker && sizePicker.getAttribute('data-figma-size-confirmed') !== 'true') return;
      if (select.selectedIndex < 0 || !select.value) return;
      // Include hidden Type sync selects — products with Metal + Size + Type need all 3
      values.push(normalizeOptionValue(select.value));
    });

    if (!values.length) return null;

    const match = variants.find((v) => {
      const opts = [v.option1, v.option2, v.option3]
        .filter((o) => o != null && String(o).length)
        .map(normalizeOptionValue);
      if (opts.length !== values.length) return false;
      return values.every((val) => opts.includes(val));
    });

    if (!match || !match.id) return null;

    root.querySelectorAll('form[data-type="add-to-cart-form"] input[name="id"], input.product-variant-id[name="id"]').forEach((input) => {
      input.disabled = false;
      input.value = String(match.id);
    });

    const selectedScript = root.querySelector('variant-selects [data-selected-variant], [data-selected-variant]');
    if (selectedScript) {
      try {
        selectedScript.textContent = JSON.stringify(match);
      } catch (_) {}
    }

    return match;
  }

  function refreshBuyboxSubmitState(scope) {
    const root =
      (scope && scope.querySelector && (scope.matches?.('product-info') || scope.tagName === 'PRODUCT-INFO') && scope) ||
      scope?.closest?.('product-info') ||
      document.querySelector('product-info');
    if (!root) return;
    const productForm = root.querySelector('product-form');
    if (!productForm || typeof productForm.toggleSubmitButton !== 'function') return;

    // Keep ADD TO BAG black & clickable — missing size/chain is validated on click, not by graying out
    let available = true;
    try {
      const script = root.querySelector('[data-selected-variant]');
      const variant = script ? JSON.parse(script.textContent || 'null') : null;
      if (variant && variant.available === false) available = false;
    } catch (_) {}

    if (!available) {
      productForm.toggleSubmitButton(true, window.variantStrings?.soldOut);
      return;
    }
    productForm.toggleSubmitButton(false);
  }

  function initAllCustomSelects(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-figma-custom-select]').forEach(initCustomSelect);
    syncChainTypeVisibility(scope);

    const roots = scope.querySelectorAll ? scope.querySelectorAll('.product, product-info') : [];
    const list = roots.length ? Array.from(roots) : [scope];
    list.forEach((productRoot) => {
      const pKey = productMemoryKey(productRoot.querySelector?.('[data-figma-chain-picker]') || productRoot);
      const sizeState = getChainSizeState(productRoot);
      const typePicker = getChainPickerByKind(productRoot, 'type');

      // Restore user-confirmed chain type after HTML swap
      if (sizeState.hasRealSize && chainTypeUserMemory.get(pKey) && typePicker) {
        const rememberedType = chainTypeValueMemory.get(pKey);
        const select = typePicker.querySelector('[data-figma-chain-native]');
        if (rememberedType && select) {
          const matchOpt = Array.from(select.options).find(
            (o) => normalizeOptionValue(o.value) === normalizeOptionValue(rememberedType)
          );
          if (matchOpt) {
            select.value = matchOpt.value;
            Array.from(select.options).forEach((o) => {
              if (o === matchOpt) o.setAttribute('selected', 'selected');
              else o.removeAttribute('selected');
            });
            typePicker.setAttribute('data-figma-chain-confirmed', 'true');
            typePicker.hidden = false;
            typePicker.style.removeProperty('display');
            syncSelectFace(select);
          }
        }
      } else if (sizeState.hasRealSize && !chainTypeUserMemory.get(pKey)) {
        resetChainTypePicker(productRoot);
        syncChainTypeVisibility(productRoot);
      }

      const matched = syncProductVariantIdFromSelections(productRoot);
      if (matched && typeof matched.price === 'number') {
        setBasePriceCents(productRoot, matched.price);
      }
      syncChainTotalPrice(productRoot);
      refreshBuyboxSubmitState(productRoot);
    });
  }

  function isNoChainValue(value) {
    const v = String(value || '')
      .trim()
      .toLowerCase();
    return !v || v === 'no chain' || v === 'none' || v === 'no-chain' || v === 'without chain';
  }

  function getChainPickerByKind(root, kind) {
    return (root || document).querySelector('[data-figma-chain-picker][data-figma-chain-kind="' + kind + '"]');
  }

  function getChainSizeState(scope) {
    const root = scope || document;
    const sizePicker = getChainPickerByKind(root, 'size');
    if (!sizePicker) {
      return { picker: null, confirmed: true, value: '', isNoChain: false, hasRealSize: true };
    }
    const confirmed = sizePicker.getAttribute('data-figma-chain-confirmed') === 'true';
    const select = sizePicker.querySelector('[data-figma-chain-native]');
    const value = select ? select.value : '';
    const isNoChain = isNoChainValue(value);
    return {
      picker: sizePicker,
      confirmed: confirmed,
      value: value,
      isNoChain: isNoChain,
      hasRealSize: confirmed && value && !isNoChain,
    };
  }

  function showChainPickerError(picker, message) {
    if (!picker) return;
    picker.classList.add('is-invalid');
    const err = picker.querySelector('[data-figma-chain-error]');
    if (err) {
      if (message) err.textContent = message;
      else if (err.getAttribute('data-default-error')) err.textContent = err.getAttribute('data-default-error');
      err.hidden = false;
    }
  }

  function clearChainPickerError(picker) {
    if (!picker) return;
    picker.classList.remove('is-invalid');
    const err = picker.querySelector('[data-figma-chain-error]');
    if (err) err.hidden = true;
  }

  function resetChainTypePicker(scope) {
    const root = scope || document;
    const typePicker = getChainPickerByKind(root, 'type');
    if (!typePicker) return;
    const select = typePicker.querySelector('[data-figma-chain-native]');
    const wrap = typePicker.querySelector('[data-figma-custom-select]');
    typePicker.setAttribute('data-figma-chain-confirmed', 'false');
    chainTypeUserMemory.set(productMemoryKey(typePicker), false);
    if (select) {
      // Native Shopify options have no empty placeholder — clear selection entirely
      // so option[selected] is gone and selectedOptionValues won't keep a phantom type.
      Array.from(select.options).forEach((o) => {
        o.removeAttribute('selected');
        o.selected = false;
      });
      try {
        select.selectedIndex = -1;
      } catch (_) {}
      select.value = '';
      syncSelectFace(select);
    }
    if (wrap) setSelectOpen(wrap, false);
    clearChainPickerError(typePicker);
    syncChainTotalPrice(root.querySelector('.product') || root);
  }

  /** No chain / empty size → hide Type. Real chain size → show Type. */
  function syncChainTypeVisibility(scope) {
    const root = scope || document;
    const typePicker = getChainPickerByKind(root, 'type');
    if (!typePicker) return;
    const sizeState = getChainSizeState(root);
    if (sizeState.hasRealSize) {
      typePicker.hidden = false;
      typePicker.removeAttribute('hidden');
      typePicker.style.removeProperty('display');
      typePicker.setAttribute('data-figma-chain-required', '');
    } else {
      typePicker.hidden = true;
      typePicker.setAttribute('hidden', '');
      typePicker.style.display = 'none';
      typePicker.removeAttribute('data-figma-chain-required');
      // Always strip type from variant selection when size is No chain / empty
      resetChainTypePicker(root);
    }
  }

  function clearChainPickerSelection(picker) {
    if (!picker) return;
    const select = picker.querySelector('[data-figma-chain-native]');
    const wrap = picker.querySelector('[data-figma-custom-select]');
    picker.setAttribute('data-figma-chain-confirmed', 'false');
    if (select) {
      const isNativeOption = Boolean(picker.hasAttribute('data-figma-chain-native-option'));
      const placeholder = Array.from(select.options).find((o) => o.disabled || o.getAttribute('data-placeholder') === 'true');
      Array.from(select.options).forEach((o) => {
        o.removeAttribute('selected');
        o.selected = false;
      });
      if (placeholder) {
        placeholder.selected = true;
        placeholder.setAttribute('selected', 'selected');
        select.value = '';
      } else if (isNativeOption) {
        try {
          select.selectedIndex = -1;
        } catch (_) {}
        select.value = '';
      } else if (select.options.length) {
        select.selectedIndex = 0;
        select.value = '';
      }
      const blockBubble = select.hasAttribute('data-figma-chain-native') && !isNativeOption;
      select.dispatchEvent(new Event('change', { bubbles: !blockBubble }));
      syncSelectFace(select);
    }
    if (wrap) setSelectOpen(wrap, false);
    clearChainPickerError(picker);
  }

  function validateChainPickers(scope) {
    const root = scope || document;
    let firstInvalid = null;
    const sizeState = getChainSizeState(root);

    root.querySelectorAll('[data-figma-chain-required]').forEach((picker) => {
      const kind = picker.getAttribute('data-figma-chain-kind') || '';
      const confirmed = picker.getAttribute('data-figma-chain-confirmed') === 'true';
      const select = picker.querySelector('[data-figma-chain-native]');
      const value = select ? select.value : '';

      // Chain type only required when a real chain size is chosen
      if (kind === 'type') {
        if (!sizeState.hasRealSize) {
          clearChainPickerError(picker);
          picker.classList.remove('is-invalid');
          return;
        }
        if (!confirmed || !value) {
          showChainPickerError(picker, 'Lütfen zincir tipinizi seçin');
          if (!firstInvalid) firstInvalid = picker;
          return;
        }
        clearChainPickerError(picker);
        return;
      }

      if (kind === 'size') {
        if (!confirmed || !value) {
          showChainPickerError(picker, 'Lütfen zincir boyunuzu seçin');
          if (!firstInvalid) firstInvalid = picker;
          return;
        }
        clearChainPickerError(picker);
        return;
      }

      if (!confirmed) {
        showChainPickerError(picker);
        if (!firstInvalid) firstInvalid = picker;
      } else {
        clearChainPickerError(picker);
      }
    });
    return firstInvalid;
  }

  function validateSizePickers(scope) {
    const root = scope || document;
    let firstInvalid = null;
    root.querySelectorAll('[data-figma-size-required]').forEach((picker) => {
      const confirmed = picker.getAttribute('data-figma-size-confirmed') === 'true';
      if (!confirmed) {
        picker.classList.add('is-invalid');
        if (!firstInvalid) firstInvalid = picker;
      } else {
        picker.classList.remove('is-invalid');
      }
    });
    return firstInvalid;
  }

  function getSelectedChainOption(picker) {
    const select = picker.querySelector('[data-figma-chain-native]');
    if (!select || !select.value) return null;
    const opt = select.options[select.selectedIndex];
    if (!opt) return null;
    let priceCents = parseInt(opt.getAttribute('data-price-cents') || '0', 10) || 0;
    let addonVariantId = opt.getAttribute('data-addon-variant-id') || '';
    const optionValue = select.value || '';

    if (priceCents > 0) {
      const mapEl = picker.querySelector('[data-figma-chain-addon-variants]');
      if (mapEl) {
        try {
          const list = JSON.parse(mapEl.textContent);
          const resolved = resolveChainAddonVariant(list, priceCents, optionValue);
          if (resolved && resolved.id) {
            addonVariantId = String(resolved.id);
            if (resolved.priceCents > 0) priceCents = resolved.priceCents;
          } else if (addonVariantId) {
            const byId = Array.isArray(list)
              ? list.find((v) => String(v.id) === String(addonVariantId))
              : null;
            if (byId && Number(byId.price) > 0) {
              priceCents = Number(byId.price);
            } else {
              addonVariantId = '';
            }
          } else {
            addonVariantId = '';
          }
        } catch (_) {}
      }
    } else {
      addonVariantId = '';
    }

    return {
      value: optionValue,
      priceCents: priceCents,
      addonVariantId: addonVariantId,
      kind: picker.getAttribute('data-figma-chain-kind') || '',
    };
  }

  /**
   * Chain 2 / Chain 3 upgrade resolver.
   * Prefer exact $10/$15, then title, then 1st/2nd paid variant (sync fee to real price).
   */
  function resolveChainAddonVariant(list, wantedCents, optionValue) {
    if (!Array.isArray(list) || !list.length) return null;
    const want = Number(wantedCents) || 0;
    if (want <= 0) return null;

    const exact = list.find((v) => Number(v.price) === want);
    if (exact && exact.id) return { id: exact.id, priceCents: want };

    const label = normalizeOptionValue(optionValue || '');
    const titleHit = list.find((v) => {
      const t = normalizeOptionValue(v.title || '');
      if (!t) return false;
      if (want === 1500 || label.includes('chain 3') || label === 'chain3') {
        return t.includes('15') || t.includes('chain 3') || t.includes('chain3') || t.includes('+15');
      }
      if (want === 1000 || label.includes('chain 2') || label === 'chain2') {
        return t.includes('10') || t.includes('chain 2') || t.includes('chain2') || t.includes('+10');
      }
      return false;
    });
    if (titleHit && titleHit.id) {
      return { id: titleHit.id, priceCents: Number(titleHit.price) || want };
    }

    const paid = list
      .filter((v) => Number(v.price) > 0 && v.id)
      .slice()
      .sort((a, b) => Number(a.price) - Number(b.price));
    if (!paid.length) return null;

    if (want === 1000 || label.includes('chain 2')) {
      return { id: paid[0].id, priceCents: Number(paid[0].price) };
    }
    if (want === 1500 || label.includes('chain 3')) {
      if (paid.length > 1) return { id: paid[1].id, priceCents: Number(paid[1].price) };
      return null;
    }
    return null;
  }

  /** Only skip type addons when Type uses native Shopify option UI (price on variant). */
  function productHasNativeTypeOption(root) {
    const scope = root || document;
    return Boolean(
      scope.querySelector('[data-figma-chain-native-option][data-figma-chain-kind="type"]')
    );
  }

  function getChainAddonItems(scope, quantity, chainGroup) {
    const root = scope || document;
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    const group = chainGroup || '';
    const skipTypeAddon = productHasNativeTypeOption(root);
    const items = [];
    root.querySelectorAll('[data-figma-chain-priced]').forEach((picker) => {
      const kind = picker.getAttribute('data-figma-chain-kind') || '';
      if (kind === 'type' && skipTypeAddon) return;
      const selected = getSelectedChainOption(picker);
      if (!selected || !selected.addonVariantId || selected.priceCents <= 0) return;
      const props = {
        _chain_upgrade: selected.kind || 'chain',
        _chain_option: selected.value,
      };
      if (group) props._chain_group = group;
      items.push({
        id: Number(selected.addonVariantId),
        quantity: qty,
        properties: props,
      });
    });
    return items;
  }

  function getMissingPricedChainOptions(scope) {
    const root = scope || document;
    const skipTypeAddon = productHasNativeTypeOption(root);
    const missing = [];
    root.querySelectorAll('[data-figma-chain-priced]').forEach((picker) => {
      const kind = picker.getAttribute('data-figma-chain-kind') || '';
      if (kind === 'type' && skipTypeAddon) return;
      const selected = getSelectedChainOption(picker);
      if (!selected || selected.priceCents <= 0) return;
      if (!selected.addonVariantId) {
        missing.push(selected.value);
      }
    });
    return missing;
  }

  function formatMoneyCents(cents) {
    const amount = Math.round((Number(cents) || 0) / 100);
    // shop.money_format may include HTML e.g. <span class=money>{{amount}}</span>
    const rawFormat = window.themeMoneyFormat || '${{amount}}';
    const format = String(rawFormat).replace(/<[^>]*>/g, '');
    const value = String(amount);
    const withCommas = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, withCommas.replace(/,/g, "'"))
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_without_trailing_zeros\s*\}\}/g, withCommas)
      .trim();
  }

  function getPriceSlots(root) {
    const scope = root || document;
    const slots = Array.from(scope.querySelectorAll('.price--figma-pdp'));
    if (slots.length) return slots;
    const sticky = document.querySelector('.sticky_pro_page .price--figma-pdp, .sticky_pro_page .price');
    return sticky ? [sticky] : [];
  }

  function setBasePriceCents(root, cents) {
    if (cents == null || Number.isNaN(Number(cents))) return;
    const value = String(cents);
    getPriceSlots(root).forEach((slot) => {
      slot.dataset.basePriceCents = value;
    });
    // Sticky ATC bar may live outside product-info
    document.querySelectorAll('.sticky_pro_page .price--figma-pdp').forEach((slot) => {
      slot.dataset.basePriceCents = value;
    });
  }

  function writePriceSlotCurrent(priceSlot, formatted) {
    if (!priceSlot) return;
    const currentEl =
      priceSlot.querySelector('.price-figma-row__current') ||
      priceSlot.querySelector('.price-item--sale') ||
      priceSlot.querySelector('.price-item--regular');
    if (!currentEl) return;
    const moneyEl = currentEl.querySelector('.money');
    if (moneyEl) moneyEl.textContent = formatted;
    else currentEl.textContent = formatted;
  }

  function getBaseVariantPriceCents(root) {
    const jsonEl = root.querySelector('[data-selected-variant], script[data-selected-variant]');
    if (jsonEl) {
      try {
        const data = JSON.parse(jsonEl.textContent);
        if (data && typeof data.price === 'number') return data.price;
      } catch (e) {
        /* ignore */
      }
    }
    const priceRoot = root.querySelector('.price--figma-pdp');
    if (priceRoot && priceRoot.dataset.basePriceCents) {
      return parseInt(priceRoot.dataset.basePriceCents, 10) || 0;
    }
    return 0;
  }

  function getChainTypeExtrasCents(root) {
    let extras = 0;
    const skipTypeExtras = productHasNativeTypeOption(root);
    root.querySelectorAll('[data-figma-chain-priced]').forEach((picker) => {
      const kind = picker.getAttribute('data-figma-chain-kind') || '';
      if (kind === 'type' && skipTypeExtras) return;
      const selected = getSelectedChainOption(picker);
      if (selected) extras += selected.priceCents;
    });
    return extras;
  }

  function syncChainTotalPrice(scope) {
    const root = scope || document.querySelector('.product-page-figma') || document;
    const priceSlots = getPriceSlots(root);
    const stickySlots = Array.from(document.querySelectorAll('.sticky_pro_page .price--figma-pdp'));
    const allSlots = priceSlots.concat(stickySlots.filter((s) => priceSlots.indexOf(s) < 0));
    if (!allSlots.length) return;

    // Always prefer live selected-variant JSON (mobile must not wait for section AJAX)
    const liveBase = getBaseVariantPriceCents(root);
    if (liveBase || liveBase === 0) {
      setBasePriceCents(root, liveBase);
    }

    allSlots.forEach((priceSlot) => {
      if (!priceSlot.dataset.basePriceCents) {
        const base = getBaseVariantPriceCents(root);
        if (base || base === 0) priceSlot.dataset.basePriceCents = String(base);
      }
      if (!priceSlot.dataset.basePriceCents) return;

      const baseCents = parseInt(priceSlot.dataset.basePriceCents || '0', 10);
      const total = baseCents + getChainTypeExtrasCents(root);
      writePriceSlotCurrent(priceSlot, formatMoneyCents(total));
    });
  }

  function bindVariantPriceBase() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    if (window.__chainPriceVariantBound) return;
    window.__chainPriceVariantBound = true;
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      const variant = event && event.data && event.data.variant;
      const sectionId = event && event.data && event.data.sectionId;
      if (!variant || typeof variant.price !== 'number') return;
      const root = sectionId
        ? document.querySelector('#shopify-section-' + sectionId) || document
        : document.querySelector('.product-page-figma') || document;
      setBasePriceCents(root, variant.price);
      // Section HTML replace can race — re-apply after paint
      syncChainTotalPrice(root);
      requestAnimationFrame(function () {
        setBasePriceCents(root, variant.price);
        syncChainTotalPrice(root);
      });
      setTimeout(function () {
        setBasePriceCents(root, variant.price);
        syncChainTotalPrice(root);
      }, 50);
      refreshNativeChainOptionPrices(root, variant);
    });
  }

  function refreshNativeChainOptionPrices(root, variant) {
    const scope = root || document;
    scope.querySelectorAll('[data-figma-chain-native-option]').forEach((picker) => {
      const mapEl = picker.querySelector('[data-figma-chain-price-map]');
      if (!mapEl) return;
      let priceMap = null;
      try {
        priceMap = JSON.parse(mapEl.textContent);
      } catch (e) {
        return;
      }
      const metalPos = parseInt(picker.getAttribute('data-metal-option-position') || '0', 10) || 0;
      let metal = '';
      if (variant && metalPos > 0) {
        metal = variant['option' + metalPos] || '';
      }
      picker.querySelectorAll('.product-buybox-figma__chain-option').forEach((li) => {
        const value = li.getAttribute('data-value') || '';
        const byMetal = priceMap[value];
        if (!byMetal || typeof byMetal !== 'object') return;
        let cents = null;
        if (metal && Object.prototype.hasOwnProperty.call(byMetal, metal)) {
          cents = byMetal[metal];
        } else {
          const vals = Object.keys(byMetal).map((k) => byMetal[k]);
          if (vals.length) cents = Math.min.apply(null, vals);
        }
        if (cents == null) return;
        li.setAttribute('data-shopify-price-cents', String(cents));
        const priceEl = li.querySelector('[data-figma-chain-shopify-price]');
        if (priceEl) priceEl.textContent = formatMoneyCents(cents);
      });
    });
  }

  window.ProductBuyboxFigma = window.ProductBuyboxFigma || {};
  window.ProductBuyboxFigma.validateChainPickers = validateChainPickers;
  window.ProductBuyboxFigma.validateSizePickers = validateSizePickers;
  window.ProductBuyboxFigma.getChainAddonItems = getChainAddonItems;
  window.ProductBuyboxFigma.getMissingPricedChainOptions = getMissingPricedChainOptions;
  window.ProductBuyboxFigma.syncChainTotalPrice = syncChainTotalPrice;
  window.ProductBuyboxFigma.resetChainTypePicker = resetChainTypePicker;
  window.ProductBuyboxFigma.syncChainTypeVisibility = syncChainTypeVisibility;
  window.ProductBuyboxFigma.getChainSizeState = getChainSizeState;
  window.ProductBuyboxFigma.afterVariantHtmlUpdate = initAllCustomSelects;
  window.ProductBuyboxFigma.syncProductVariantIdFromSelections = syncProductVariantIdFromSelections;
  window.ProductBuyboxFigma.refreshBuyboxSubmitState = refreshBuyboxSubmitState;
  window.ProductBuyboxFigma.closeAllSelectPanels = closeAllSelectPanels;
  window.ProductBuyboxFigma.isChainTypeUserConfirmed = function (productId) {
    return Boolean(chainTypeUserMemory.get(String(productId || 'product')));
  };

  document.addEventListener('change', (e) => {
    if (!(e.target.matches && e.target.matches('[data-figma-native-select]'))) return;

    const select = e.target;
    syncSelectFace(select);

    const sizePicker = select.closest('[data-figma-size-picker]');
    if (sizePicker && select.value && select.getAttribute('data-placeholder') !== 'true') {
      const opt = select.options[select.selectedIndex];
      if (opt && opt.getAttribute('data-placeholder') !== 'true') {
        sizePicker.setAttribute('data-figma-size-confirmed', 'true');
        const sizeKey = selectMemoryKey(select);
        if (sizeKey) sizeConfirmedMemory.set(sizeKey, select.value);
      }
    }

    if (!select.hasAttribute('data-figma-chain-native')) {
      refreshBuyboxSubmitState(select.closest('product-info') || select.closest('.product') || document);
      return;
    }

    const productRoot = select.closest('.product') || select.closest('product-info') || document;
    const picker = select.closest('[data-figma-chain-picker]');
    const kind = picker ? picker.getAttribute('data-figma-chain-kind') || '' : '';

    if (picker && select.value) {
      const opt = select.options[select.selectedIndex];
      if (!opt || opt.getAttribute('data-placeholder') === 'true') return;

      picker.setAttribute('data-figma-chain-confirmed', 'true');
      clearChainPickerError(picker);

      if (kind === 'size') {
        chainTypeUserMemory.set(productMemoryKey(select), false);
        chainTypeValueMemory.delete(productMemoryKey(select));
        resetChainTypePicker(productRoot);
        syncChainTypeVisibility(productRoot);
      }
      if (kind === 'type') {
        chainTypeUserMemory.set(productMemoryKey(select), true);
        chainTypeValueMemory.set(productMemoryKey(select), select.value);
      }
    }

    syncChainTotalPrice(productRoot);
    syncProductVariantIdFromSelections(productRoot);
    refreshBuyboxSubmitState(productRoot);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      bindVariantPriceBase();
      syncChainTotalPrice(document.querySelector('.product-page-figma') || document);
    });
  } else {
    bindVariantPriceBase();
    syncChainTotalPrice(document.querySelector('.product-page-figma') || document);
  }

  function toggleCustomSelectFromFace(wrap, face) {
    if (!wrap || !face) return;
    const panel = wrap.querySelector('.product-buybox-figma__select-panel');
    const select = wrap.querySelector('[data-figma-native-select]');
    if (!panel || !select) return;

    const chainPicker = wrap.closest('[data-figma-chain-picker]');
    const chainKind = chainPicker ? chainPicker.getAttribute('data-figma-chain-kind') || '' : '';

    if (chainKind === 'type') {
      const productRoot = wrap.closest('.product') || wrap.closest('product-info') || document;
      const sizeState = getChainSizeState(productRoot);
      if (!sizeState.hasRealSize) {
        closeAllSelectPanels();
        const sizePicker = sizeState.picker || getChainPickerByKind(productRoot, 'size');
        showChainPickerError(sizePicker, 'Lütfen zincir boyunuzu seçin');
        return;
      }
    }

    const open = face.getAttribute('aria-expanded') === 'true';
    closeAllSelectPanels();
    if (!open) setSelectOpen(wrap, true);
  }

  function applyCustomSelectOption(wrap, optionLi) {
    if (!wrap || !optionLi) return false;
    if (optionLi.getAttribute('data-placeholder') === 'true') return false;

    const select = wrap.querySelector('[data-figma-native-select]');
    if (!select) return false;

    const faceLabelEl = optionLi.querySelector(
      '.product-buybox-figma__chain-option__label, .product-buybox-figma__select-option-label'
    );
    const faceLabel = (faceLabelEl ? faceLabelEl.textContent : optionLi.textContent || '').trim();

    // CRITICAL: index first — select.value with inch quotes (18") fails on iOS Safari
    let opt = null;
    const rawIdx = parseInt(optionLi.getAttribute('data-option-index') || '-1', 10);
    if (rawIdx >= 0) {
      const hasPlaceholder =
        select.options[0] && select.options[0].getAttribute('data-placeholder') === 'true';
      const optIndex = hasPlaceholder ? rawIdx + 1 : rawIdx;
      if (select.options[optIndex]) opt = select.options[optIndex];
    }
    if (!opt) opt = findOptionForListItem(select, optionLi);
    if (!opt) {
      if (faceLabel) paintChainFace(wrap, faceLabel);
      return false;
    }
    if (opt.getAttribute('data-placeholder') === 'true') return false;
    if (!opt.value && opt.disabled) return false;

    // Sold-out Shopify options may be disabled — still allow pick so UI updates
    if (opt.disabled) {
      opt.disabled = false;
      opt.removeAttribute('disabled');
    }

    const value = opt.value;
    const picker = wrap.closest('[data-figma-chain-picker]');
    const chainKind = picker ? picker.getAttribute('data-figma-chain-kind') || '' : '';
    const productRoot = wrap.closest('.product') || wrap.closest('product-info') || document;

    if (chainKind === 'type') {
      const sizeState = getChainSizeState(productRoot);
      if (!sizeState.hasRealSize) {
        setSelectOpen(wrap, false);
        const sizePicker = sizeState.picker || getChainPickerByKind(productRoot, 'size');
        showChainPickerError(sizePicker, 'Lütfen zincir boyunuzu seçin');
        return false;
      }
    }

    wrap._figmaJustPicked = Date.now();

    // Same value again: just close
    const clickedId = optionLi.getAttribute('data-option-value-id') || '';
    const selectedOpt = select.options[select.selectedIndex];
    const selectedId = selectedOpt ? selectedOpt.getAttribute('data-option-value-id') || '' : '';
    const sameById = clickedId && selectedId && clickedId === selectedId;
    const sameByRef = selectedOpt === opt;
    const alreadySelected =
      picker &&
      picker.getAttribute('data-figma-chain-confirmed') === 'true' &&
      (sameById || sameByRef);
    if (alreadySelected) {
      if (faceLabel) paintChainFace(wrap, faceLabel);
      wrap.querySelectorAll('.product-buybox-figma__chain-option, .product-buybox-figma__select-option').forEach((li) => {
        li.setAttribute('aria-selected', li === optionLi ? 'true' : 'false');
      });
      // Brief buffer so the synthetic mobile click is absorbed by the still-open panel
      setTimeout(() => {
        setSelectOpen(wrap, false);
      }, 50);
      syncSelectFace(select);
      return true;
    }

    const liCents =
      optionLi.getAttribute('data-price-cents') || optionLi.getAttribute('data-shopify-price-cents');
    if (liCents != null && liCents !== '') opt.setAttribute('data-price-cents', liCents);
    const liAddon = optionLi.getAttribute('data-addon-variant-id');
    if (liAddon) opt.setAttribute('data-addon-variant-id', liAddon);

    if (faceLabel && (chainKind === 'size' || chainKind === 'type')) {
      paintChainFace(wrap, faceLabel);
    }

    if (picker && chainKind === 'size') {
      rememberChainSize(picker, opt, optionLi);
      picker.setAttribute('data-figma-chain-confirmed', 'true');
    }

    // SESSİZCE radyo eşitle — yalnızca native Shopify option picker'larda.
    // Property-based Chain Type + upgrade addon birlikteyken variant'a yazmak fiyatı çiftler.
    // Ama üründe gerçek Type option varsa (gizli sync), addon KULLANILMAZ — option senkronize edilir.
    const variantSelects = wrap.closest('variant-selects');
    const isNativeOptionPicker = Boolean(wrap.closest('[data-figma-chain-native-option]'));
    let matchedRadio = null;
    if (isNativeOptionPicker && variantSelects && value) {
      const targetVal = normalizeOptionValue(value);
      variantSelects.querySelectorAll('input[type="radio"]').forEach((radio) => {
        if (normalizeOptionValue(radio.value) === targetVal) {
          radio.checked = true;
          matchedRadio = radio;
        }
      });
    }

    forceSelectOption(select, opt);
    const forcedIdx = Array.from(select.options).indexOf(opt);
    if (forcedIdx >= 0) {
      try {
        select.selectedIndex = forcedIdx;
      } catch (_) {}
    }
    for (let i = 0; i < select.options.length; i++) {
      const o = select.options[i];
      const on = i === forcedIdx;
      o.selected = on;
      if (on) o.setAttribute('selected', 'selected');
      else o.removeAttribute('selected');
    }

    if (picker) {
      picker.setAttribute('data-figma-chain-confirmed', 'true');
      clearChainPickerError(picker);
      if (chainKind === 'type') {
        chainTypeUserMemory.set(productMemoryKey(picker), true);
        chainTypeValueMemory.set(productMemoryKey(picker), value);
      }
      if (chainKind === 'size') {
        chainTypeUserMemory.set(productMemoryKey(picker), false);
        chainTypeValueMemory.delete(productMemoryKey(picker));
      }
    }

    const sizePicker = wrap.closest('[data-figma-size-picker]');
    if (sizePicker) {
      sizePicker.setAttribute('data-figma-size-confirmed', 'true');
      const sizeKey = selectMemoryKey(select);
      if (sizeKey) sizeConfirmedMemory.set(sizeKey, value);
    }

    const isChainNative = select.hasAttribute('data-figma-chain-native');
    const nativeChainPicker = wrap.closest('[data-figma-chain-native-option]');
    // Always bubble for native Shopify options so mobile variant AJAX + price update fire
    const blockBubble = isChainNative && !nativeChainPicker;

    if (chainKind === 'size') {
      resetChainTypePicker(productRoot);
    }

    const matchedVariant = syncProductVariantIdFromSelections(productRoot);
    if (matchedVariant && typeof matchedVariant.price === 'number') {
      setBasePriceCents(productRoot, matchedVariant.price);
    }
    syncSelectFace(select);
    if (faceLabel && (chainKind === 'size' || chainKind === 'type')) {
      paintChainFace(wrap, faceLabel);
    }

    wrap.querySelectorAll('.product-buybox-figma__chain-option, .product-buybox-figma__select-option').forEach((li) => {
      li.setAttribute('aria-selected', li === optionLi ? 'true' : 'false');
    });

    // Ring / bracelet size is already resolved to the exact variant above.
    // Do not trigger Shopify's section AJAX for this custom picker: iOS Safari can
    // repaint the picker from the response with option 1 (US 4) selected again.
    // Metal/other native option changes still use the normal Shopify update path.
    if (matchedRadio) {
      matchedRadio.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (sizePicker) {
      syncSelectFace(select);
      refreshBuyboxSubmitState(productRoot);
    } else {
      select.dispatchEvent(new Event('change', { bubbles: !blockBubble }));
    }

    // Immediate price paint (mobile often lags or misses section AJAX)
    syncChainTotalPrice(productRoot);
    requestAnimationFrame(function () {
      const again = syncProductVariantIdFromSelections(productRoot);
      if (again && typeof again.price === 'number') setBasePriceCents(productRoot, again.price);
      syncChainTotalPrice(productRoot);
    });

    setTimeout(() => {
      setSelectOpen(wrap, false);
    }, 50);

    if (chainKind === 'size') {
      syncChainTypeVisibility(productRoot);
    }

    refreshBuyboxSubmitState(productRoot);
    return true;
  }

  // Outside click closes open panels
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-figma-custom-select]')) return;
    if (
      document.body.classList.contains('buybox-modal-scroll-lock') ||
      e.target.closest('[data-engraving-modal], [data-size-guide-modal], [data-metal-guide-modal]')
    ) {
      return;
    }
    closeAllSelectPanels();
  });

  function releaseBodyModalLock() {
    var engravingOpen = document.querySelector('[data-engraving-modal]:not([hidden])');
    var sizeGuideOpen = document.querySelector('[data-size-guide-modal]:not([hidden])');
    var metalGuideOpen = document.querySelector('[data-metal-guide-modal]:not([hidden])');
    if (!engravingOpen && !sizeGuideOpen && !metalGuideOpen) {
      unlockBuyboxBodyScroll();
    }
  }

  /** iOS-safe page scroll lock (same pattern as menu drawer) */
  function lockBuyboxBodyScroll() {
    if (document.body.classList.contains('buybox-modal-scroll-lock')) return;
    var scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.dataset.buyboxModalScrollY = String(scrollY);
    document.documentElement.classList.add('buybox-modal-scroll-lock');
    document.body.classList.add(
      'product-buybox-figma--modal-open',
      'modal-open-lock',
      'buybox-modal-scroll-lock'
    );
    document.body.style.top = '-' + scrollY + 'px';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.left = '0';
    document.body.style.right = '0';
  }

  function unlockBuyboxBodyScroll() {
    if (!document.body.classList.contains('buybox-modal-scroll-lock')) {
      document.body.classList.remove('product-buybox-figma--modal-open', 'modal-open-lock');
      return;
    }
    var scrollY = parseInt(document.body.dataset.buyboxModalScrollY || '0', 10) || 0;
    document.documentElement.classList.remove('buybox-modal-scroll-lock');
    document.body.classList.remove(
      'product-buybox-figma--modal-open',
      'modal-open-lock',
      'buybox-modal-scroll-lock'
    );
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.left = '';
    document.body.style.right = '';
    delete document.body.dataset.buyboxModalScrollY;
    window.scrollTo(0, scrollY);
  }

  function getMetalGuideTrigger(modal) {
    if (!modal || !modal.id) return null;
    return document.querySelector('[data-metal-guide-open][aria-controls="' + modal.id + '"]');
  }

  function openMetalGuideModal(modal, trigger) {
    if (!modal) return;
    closeAllSelectPanels();
    modal.hidden = false;
    lockBuyboxBodyScroll();
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    if (modal.hasAttribute('data-metal-guide-sheet')) {
      initMetalGuideSheetDrag(modal);
      modal.classList.remove('is-sheet-open', 'is-sheet-dragging');
      var panel = modal.querySelector('[data-metal-guide-sheet-panel]');
      if (panel) panel.style.transform = '';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          modal.classList.add('is-sheet-open');
        });
      });
    }
  }

  function closeMetalGuideModal(modal, trigger) {
    if (!modal) return;

    if (modal.hasAttribute('data-metal-guide-sheet') && window.matchMedia('(max-width: 749px)').matches) {
      modal.classList.remove('is-sheet-open', 'is-sheet-dragging');
      var panel = modal.querySelector('[data-metal-guide-sheet-panel]');
      if (panel) panel.style.transform = '';
      var finished = false;
      var finishClose = function () {
        if (finished) return;
        finished = true;
        panel && panel.removeEventListener('transitionend', onEnd);
        modal.hidden = true;
        releaseBodyModalLock();
        var t = trigger || getMetalGuideTrigger(modal);
        if (t) {
          t.setAttribute('aria-expanded', 'false');
          t.focus();
        }
      };
      var onEnd = function (ev) {
        if (ev.target !== panel || ev.propertyName !== 'transform') return;
        finishClose();
      };
      if (panel) panel.addEventListener('transitionend', onEnd);
      setTimeout(finishClose, 360);
      return;
    }

    modal.hidden = true;
    releaseBodyModalLock();
    var t = trigger || getMetalGuideTrigger(modal);
    if (t) {
      t.setAttribute('aria-expanded', 'false');
      t.focus();
    }
  }

  function initMetalGuideSheetDrag(modal) {
    if (!modal || modal.dataset.metalGuideSheetDragBound === '1') return;
    var panel = modal.querySelector('[data-metal-guide-sheet-panel]');
    var handle = modal.querySelector('[data-metal-guide-sheet-handle]');
    if (!panel || !handle) return;
    modal.dataset.metalGuideSheetDragBound = '1';

    var startY = 0;
    var currentY = 0;
    var dragging = false;

    var onStart = function (clientY) {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      dragging = true;
      startY = clientY;
      currentY = 0;
      modal.classList.add('is-sheet-dragging');
    };

    var onMove = function (clientY) {
      if (!dragging) return;
      currentY = Math.max(0, clientY - startY);
      panel.style.transform = 'translate3d(0, ' + currentY + 'px, 0)';
    };

    var onEnd = function () {
      if (!dragging) return;
      dragging = false;
      modal.classList.remove('is-sheet-dragging');
      if (currentY > 90) {
        panel.style.transform = '';
        closeMetalGuideModal(modal, getMetalGuideTrigger(modal));
        return;
      }
      panel.style.transform = '';
      modal.classList.add('is-sheet-open');
    };

    handle.addEventListener('touchstart', function (e) {
      if (!e.touches || !e.touches[0]) return;
      onStart(e.touches[0].clientY);
    }, { passive: true });
    handle.addEventListener('touchmove', function (e) {
      if (!e.touches || !e.touches[0]) return;
      onMove(e.touches[0].clientY);
    }, { passive: true });
    handle.addEventListener('touchend', onEnd);
    handle.addEventListener('touchcancel', onEnd);
  }

  function getSizeGuideTrigger(modal) {
    if (!modal || !modal.id) return null;
    return document.querySelector('[data-size-guide-open][aria-controls="' + modal.id + '"]');
  }

  function openSizeGuideModal(modal, trigger) {
    if (!modal) return;
    closeAllSelectPanels();
    modal.hidden = false;
    lockBuyboxBodyScroll();
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    if (modal.hasAttribute('data-size-guide-sheet')) {
      initSizeGuideSheetDrag(modal);
      modal.classList.remove('is-sheet-open', 'is-sheet-dragging');
      var panel = modal.querySelector('[data-size-guide-sheet-panel]');
      if (panel) panel.style.transform = '';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          modal.classList.add('is-sheet-open');
        });
      });
    }
  }

  function closeSizeGuideModal(modal, trigger) {
    if (!modal) return;

    if (modal.hasAttribute('data-size-guide-sheet') && window.matchMedia('(max-width: 749px)').matches) {
      modal.classList.remove('is-sheet-open', 'is-sheet-dragging');
      var panel = modal.querySelector('[data-size-guide-sheet-panel]');
      if (panel) panel.style.transform = '';
      var finished = false;
      var finishClose = function () {
        if (finished) return;
        finished = true;
        panel && panel.removeEventListener('transitionend', onEnd);
        modal.hidden = true;
        releaseBodyModalLock();
        var t = trigger || getSizeGuideTrigger(modal);
        if (t) {
          t.setAttribute('aria-expanded', 'false');
          t.focus();
        }
      };
      var onEnd = function (ev) {
        if (ev.target !== panel || ev.propertyName !== 'transform') return;
        finishClose();
      };
      if (panel) panel.addEventListener('transitionend', onEnd);
      setTimeout(finishClose, 360);
      return;
    }

    modal.hidden = true;
    releaseBodyModalLock();
    var t = trigger || getSizeGuideTrigger(modal);
    if (t) {
      t.setAttribute('aria-expanded', 'false');
      t.focus();
    }
  }

  function initSizeGuideSheetDrag(modal) {
    if (!modal || modal.dataset.sizeGuideSheetDragBound === '1') return;
    var panel = modal.querySelector('[data-size-guide-sheet-panel]');
    var handle = modal.querySelector('[data-size-guide-sheet-handle]');
    if (!panel || !handle) return;
    modal.dataset.sizeGuideSheetDragBound = '1';

    var startY = 0;
    var currentY = 0;
    var dragging = false;

    var onStart = function (clientY) {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      dragging = true;
      startY = clientY;
      currentY = 0;
      modal.classList.add('is-sheet-dragging');
    };

    var onMove = function (clientY) {
      if (!dragging) return;
      currentY = Math.max(0, clientY - startY);
      panel.style.transform = 'translate3d(0, ' + currentY + 'px, 0)';
    };

    var onEnd = function () {
      if (!dragging) return;
      dragging = false;
      modal.classList.remove('is-sheet-dragging');
      if (currentY > 110) {
        panel.style.transform = '';
        closeSizeGuideModal(modal, getSizeGuideTrigger(modal));
        return;
      }
      panel.style.transform = '';
      modal.classList.add('is-sheet-open');
    };

    handle.addEventListener(
      'touchstart',
      function (e) {
        if (!e.touches || !e.touches[0]) return;
        onStart(e.touches[0].clientY);
      },
      { passive: true }
    );
    handle.addEventListener(
      'touchmove',
      function (e) {
        if (!dragging || !e.touches || !e.touches[0]) return;
        onMove(e.touches[0].clientY);
      },
      { passive: true }
    );
    handle.addEventListener('touchend', onEnd);
    handle.addEventListener('touchcancel', onEnd);
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-size-guide-sheet]').forEach(initSizeGuideSheetDrag);
  });
  document.addEventListener('shopify:section:load', function (e) {
    if (!e.target || !e.target.querySelectorAll) return;
    e.target.querySelectorAll('[data-size-guide-sheet]').forEach(initSizeGuideSheetDrag);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    closeAllSelectPanels();
    var openMetalGuide = document.querySelector('[data-metal-guide-modal]:not([hidden])');
    if (openMetalGuide) {
      closeMetalGuideModal(openMetalGuide, getMetalGuideTrigger(openMetalGuide));
      return;
    }
    var openSizeGuide = document.querySelector('[data-size-guide-modal]:not([hidden])');
    if (openSizeGuide) {
      closeSizeGuideModal(openSizeGuide, getSizeGuideTrigger(openSizeGuide));
      return;
    }
    var openModal = document.querySelector('[data-engraving-modal]:not([hidden])');
    if (openModal) {
      var root = openModal.closest('.product-custom-engraving-field');
      var trigger = root && root.querySelector('[data-engraving-open]');
      closeEngravingModal(openModal, trigger);
    }
  });

  var ENGRAVING_NOTES_MIN_HEIGHT_PX = 123;

  function resizeEngravingNotesTextarea(ta) {
    if (!ta || ta.tagName !== 'TEXTAREA') return;
    ta.style.height = 'auto';
    ta.style.height = Math.max(ENGRAVING_NOTES_MIN_HEIGHT_PX, ta.scrollHeight) + 'px';
  }

  /** Engraving modal */
  var ENGRAVING_PREVIEW_PLACEHOLDER = 'Your engraving preview';

  function getEngravingTextInput(modal) {
    if (!modal) return null;
    return modal.querySelector('[data-engraving-text-input]') || modal.querySelector('#custom_engraving');
  }

  function syncEngravingFigmaUI(modal) {
    if (!modal) return;
    var kind = modal.getAttribute('data-engraving-kind');
    if (kind !== 'necklace' && kind !== 'ring') return;
    var input = getEngravingTextInput(modal);
    var counter = modal.querySelector('[data-engraving-counter]');
    var preview = modal.querySelector('[data-engraving-preview]');
    var saveBtn = modal.querySelector('[data-engraving-save]');
    var activeOpt = modal.querySelector('[data-engraving-font-option].is-active');
    var defaultMax = kind === 'ring' ? 20 : 24;
    var max = activeOpt ? parseInt(activeOpt.getAttribute('data-font-max') || String(defaultMax), 10) : defaultMax;
    var isScript = activeOpt && String(activeOpt.dataset.fontValue || '').toLowerCase() === 'script';
    var value = input ? String(input.value || '') : '';

    if (input) {
      input.setAttribute('maxlength', String(max));
      if (value.length > max) {
        input.value = value.slice(0, max);
        value = input.value;
      }
    }

    if (counter) counter.textContent = value.length + ' / ' + max;

    if (preview) {
      var filled = value.trim().length > 0;
      preview.textContent = filled ? value : ENGRAVING_PREVIEW_PLACEHOLDER;
      preview.classList.toggle('is-filled', filled);
      preview.classList.toggle('is-script', !!isScript);
    }

    if (saveBtn) saveBtn.disabled = value.trim().length === 0;
  }

  function openEngravingModal(modal, trigger) {
    if (!modal) return;
    modal.hidden = false;
    lockBuyboxBodyScroll();
    const input = getEngravingTextInput(modal);
    const notesTa = modal.querySelector('[data-engraving-notes-autosize]');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    syncEngravingFigmaUI(modal);
    requestAnimationFrame(() => {
      if (notesTa) resizeEngravingNotesTextarea(notesTa);
      if (input) {
        input.focus();
        try {
          const len = input.value.length;
          input.setSelectionRange(len, len);
        } catch (_) {}
      }
    });
  }

  function closeEngravingModal(modal, trigger) {
    if (!modal) return;
    modal.hidden = true;
    releaseBodyModalLock();
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    // Do not focus trigger on touch devices — delayed focus + click can hit ADD TO BAG underneath
    if (trigger && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      trigger.focus();
    }
  }

  function setEngravingFontOption(optionBtn) {
    if (!optionBtn) return;
    const modal = optionBtn.closest('[data-engraving-modal]');
    if (!modal) return;
    const value = optionBtn.dataset.fontValue || optionBtn.textContent.trim();
    const hiddenInput = modal.querySelector('[data-engraving-font-input]');
    const options = modal.querySelectorAll('[data-engraving-font-option]');
    options.forEach((btn) => {
      const active = btn === optionBtn;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-checked', active ? 'true' : 'false');
    });
    if (hiddenInput) hiddenInput.value = value;
    syncEngravingFigmaUI(modal);
  }

  document.addEventListener('input', (e) => {
    const ta = e.target.closest('[data-engraving-notes-autosize]');
    if (ta && ta.tagName === 'TEXTAREA') resizeEngravingNotesTextarea(ta);

    const engInput = e.target.closest('[data-engraving-text-input], #custom_engraving');
    if (engInput) {
      const modal = engInput.closest('[data-engraving-modal]');
      syncEngravingFigmaUI(modal);
    }
  });

  document.addEventListener('click', (e) => {
    const metalGuideOpenBtn = e.target.closest('[data-metal-guide-open]');
    if (metalGuideOpenBtn) {
      e.preventDefault();
      const id = metalGuideOpenBtn.getAttribute('aria-controls');
      const modal = id ? document.getElementById(id) : null;
      openMetalGuideModal(modal, metalGuideOpenBtn);
      return;
    }

    const metalGuideCloseBtn = e.target.closest('[data-metal-guide-close]');
    if (metalGuideCloseBtn) {
      e.preventDefault();
      const modal = metalGuideCloseBtn.closest('[data-metal-guide-modal]');
      closeMetalGuideModal(modal, getMetalGuideTrigger(modal));
      return;
    }

    const sizeGuideOpenBtn = e.target.closest('[data-size-guide-open]');
    if (sizeGuideOpenBtn) {
      e.preventDefault();
      const id = sizeGuideOpenBtn.getAttribute('aria-controls');
      const modal = id ? document.getElementById(id) : null;
      openSizeGuideModal(modal, sizeGuideOpenBtn);
      return;
    }

    const sizeGuideCloseBtn = e.target.closest('[data-size-guide-close]');
    if (sizeGuideCloseBtn) {
      e.preventDefault();
      const modal = sizeGuideCloseBtn.closest('[data-size-guide-modal]');
      closeSizeGuideModal(modal, getSizeGuideTrigger(modal));
      return;
    }

    const ringRegionTab = e.target.closest('[data-ring-region]');
    if (ringRegionTab) {
      e.preventDefault();
      const chart = ringRegionTab.closest('[data-ring-region-tabs]');
      if (!chart) return;
      const region = ringRegionTab.getAttribute('data-ring-region');
      chart.querySelectorAll('[data-ring-region]').forEach((tab) => {
        const active = tab === ringRegionTab;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      chart.querySelectorAll('[data-ring-region-panel]').forEach((panel) => {
        const match = panel.getAttribute('data-ring-region-panel') === region;
        panel.classList.toggle('is-active', match);
        panel.hidden = !match;
      });
      return;
    }

    const fontBtn = e.target.closest('[data-engraving-font-option]');
    if (fontBtn) {
      e.preventDefault();
      setEngravingFontOption(fontBtn);
      return;
    }

    const openBtn = e.target.closest('[data-engraving-open]');
    if (openBtn) {
      e.preventDefault();
      // Chain Size panel open — don't open engraving underneath
      if (document.body.classList.contains('buybox-select-open')) return;
      if (openBtn.disabled || openBtn.classList.contains('is-locked')) return;
      const id = openBtn.getAttribute('aria-controls');
      const modal = id ? document.getElementById(id) : openBtn.parentElement && openBtn.parentElement.querySelector('[data-engraving-modal]');
      openEngravingModal(modal, openBtn);
      return;
    }

    const closeBtn = e.target.closest('[data-engraving-close]');
    if (closeBtn) {
      const modal = closeBtn.closest('[data-engraving-modal]');
      const root = modal && modal.closest('.product-custom-engraving-field');
      const trigger = root && root.querySelector('[data-engraving-open]');
      closeEngravingModal(modal, trigger);
      return;
    }

    const skipBtn = e.target.closest('[data-engraving-skip]');
    if (skipBtn) {
      e.preventDefault();
      const modal = skipBtn.closest('[data-engraving-modal]');
      const root = modal && modal.closest('.product-custom-engraving-field');
      const trigger = root && root.querySelector('[data-engraving-open]');
      const input = getEngravingTextInput(modal);
      const fontInput = modal && modal.querySelector('[data-engraving-font-input]');
      const kind = modal && modal.getAttribute('data-engraving-kind');
      if (input) input.value = '';
      if (fontInput && (kind === 'necklace' || kind === 'ring')) {
        fontInput.value = 'Print';
        const printOpt = modal.querySelector('[data-engraving-font-option][data-font-value="Print"]');
        if (printOpt) setEngravingFontOption(printOpt);
        else syncEngravingFigmaUI(modal);
      } else {
        syncEngravingFigmaUI(modal);
      }
      closeEngravingModal(modal, trigger);
      return;
    }

    const saveBtn = e.target.closest('[data-engraving-save]');
    if (saveBtn) {
      if (saveBtn.disabled) return;
      const modal = saveBtn.closest('[data-engraving-modal]');
      const root = modal && modal.closest('.product-custom-engraving-field');
      const trigger = root && root.querySelector('[data-engraving-open]');
      closeEngravingModal(modal, trigger);
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    initAllCustomSelects(document);
  });

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target && e.target.querySelectorAll) initAllCustomSelects(e.target);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, () => {
      requestAnimationFrame(() => initAllCustomSelects(document));
    });

    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      resolveCartItemCount(event)
        .then((itemCount) => {
          if (document.querySelector('.product-buybox-figma__upsell')) {
            updateUnlockGiftProgress(itemCount);
          }
        })
        .catch(() => {});
    });
  }

  function resolveCartItemCount(event) {
    const countVisible = (cart) => {
      if (!cart || !cart.items) return 0;
      return cart.items.reduce((sum, item) => {
        const props = item.properties || {};
        if (props._chain_upgrade) return sum;
        const handle = String(item.handle || '').toLowerCase();
        if (handle === 'chain-size-upgrade' || handle === 'chain-type-upgrade') return sum;
        return sum + (item.quantity || 0);
      }, 0);
    };

    if (event && event.cartData && Array.isArray(event.cartData.items)) {
      return Promise.resolve(countVisible(event.cartData));
    }

    const cartUrl = window.routes && window.routes.cart_url ? `${window.routes.cart_url}.js` : '/cart.js';
    return fetch(cartUrl)
      .then((response) => response.json())
      .then((cart) => countVisible(cart));
  }

  function updateUnlockCard(card, unlocked) {
    if (!card) return;

    card.classList.toggle('is-unlocked', unlocked);
    card.classList.toggle('is-locked', !unlocked);

    const stateSpan = card.querySelector('.product-buybox-figma__unlock-state span');
    if (stateSpan) stateSpan.textContent = unlocked ? 'Unlocked' : 'Locked';

    const overlay = card.querySelector('.product-buybox-figma__unlock-lock-overlay');
    if (unlocked && overlay) overlay.remove();
  }

  function updateUnlockGiftProgress(itemCount) {
    let unlockScore = Number(itemCount) + 1;
    if (Number.isNaN(unlockScore)) unlockScore = 1;
    if (unlockScore < 1) unlockScore = 1;
    if (unlockScore > 3) unlockScore = 3;

    const progressPercent = (unlockScore * 100) / 3;

    document.querySelectorAll('.product-buybox-figma__upsell').forEach((root) => {
      root.style.setProperty('--unlock-progress', `${progressPercent}%`);

      const cards = root.querySelectorAll('.product-buybox-figma__unlock-card');
      if (cards.length < 3) return;

      cards[0].classList.add('is-unlocked');
      cards[0].classList.remove('is-locked');

      updateUnlockCard(cards[1], unlockScore >= 2);
      updateUnlockCard(cards[2], unlockScore >= 3);
    });
  }
})();
